import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { Switch } from '@rekrytering/ui/components/switch'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBillingActions } from '@/features/billing/hooks/use-billing-actions'
import type { Subscription, User, Wallet } from '~/types'

interface NormalizedProduct {
  readonly id: string
  readonly name: string
  readonly amountCents: number
  readonly currency: string
}

interface NormalizedProducts {
  readonly topUpProducts: NormalizedProduct[]
  readonly subscriptionProducts: NormalizedProduct[]
}

function useNormalizedProducts(): NormalizedProducts {
  const { data: products } = useQuery(
    convexQuery(api.features.billing.polar.listAllProducts, {}),
  )
  const productsArray = products ?? []

  return {
    topUpProducts: productsArray
      .filter((p) => !p.isRecurring)
      .map((p) => ({
        id: p.id,
        name: p.name,
        amountCents: p.prices[0]?.priceAmount ?? 0,
        currency: p.prices[0]?.priceCurrency ?? 'usd',
      })),
    subscriptionProducts: productsArray
      .filter((p) => p.isRecurring)
      .map((p) => ({
        id: p.id,
        name: p.name,
        amountCents: p.prices[0]?.priceAmount ?? 0,
        currency: p.prices[0]?.priceCurrency ?? 'usd',
      })),
  }
}

interface TopUpProductCardProps {
  readonly product: NormalizedProduct
  readonly selected: boolean
  readonly onSelect: (id: string) => void
}

function TopUpProductCard({
  product,
  selected,
  onSelect,
}: TopUpProductCardProps) {
  return (
    <div
      tabIndex={0}
      role="button"
      className={`flex w-full select-none items-center justify-between rounded-md border border-border p-4 hover:border-primary/60 ${
        selected ? 'border-primary/60' : ''
      }`}
      onClick={() => onSelect(product.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSelect(product.id)
      }}
    >
      <span className="text-base font-medium text-primary">{product.name}</span>
      <span className="rounded-md bg-primary/10 px-1.5 text-sm font-medium text-primary/80">
        {(product.amountCents / 100).toFixed(0)}{' '}
        {product.currency.toUpperCase()}
      </span>
    </div>
  )
}

interface BalanceSectionProps {
  readonly wallet: Wallet
}

function BalanceSection({ wallet }: BalanceSectionProps) {
  const { t } = useTranslation()
  const balanceFormatted = (wallet.balanceCents / 100).toFixed(2)

  return (
    <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
      <div className="flex flex-col gap-2 p-6">
        <h2 className="text-xl font-medium text-primary">
          {t('billing.balanceTitle')}
        </h2>
        <p className="text-3xl font-semibold text-primary">
          ${balanceFormatted}
        </p>
        <p className="text-sm font-normal text-primary/60">
          {t('billing.balanceDescription')}
        </p>
      </div>
    </div>
  )
}

interface SubscriptionSectionProps {
  readonly subscription: Subscription | undefined
  readonly subscriptionProducts: NormalizedProduct[]
  readonly onSubscribe: (productId: string) => Promise<void>
  readonly onManage: () => Promise<void>
  readonly isOwner: boolean
}

function SubscriptionSection({
  subscription,
  subscriptionProducts,
  onSubscribe,
  onManage,
  isOwner,
}: SubscriptionSectionProps) {
  const { t } = useTranslation()
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<
    string | undefined
  >(undefined)
  const isActive =
    subscription?.status === 'active' || subscription?.status === 'trialing'

  if (isActive && subscription) {
    return (
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-2 p-6">
          <h2 className="text-xl font-medium text-primary">
            {t('billing.subscriptionTitle')}
          </h2>
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                subscription.status === 'trialing'
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                  : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              }`}
            >
              {subscription.status === 'trialing'
                ? t('billing.subscriptionStatusTrialing')
                : t('billing.subscriptionStatusActive')}
            </span>
          </div>
          {subscription.status === 'trialing' && subscription.trialEndsAt && (
            <p className="text-sm font-normal text-primary/60">
              {t('billing.trialEndsOn', {
                date: new Date(subscription.trialEndsAt).toLocaleDateString(),
              })}
            </p>
          )}
        </div>
        {isOwner && (
          <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 py-3 dark:bg-card">
            <p className="text-sm font-normal text-primary/60">
              {t('billing.redirectedToPolar')}
            </p>
            <Button type="button" size="sm" onClick={onManage}>
              {t('billing.manageSubscription')}
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
      <div className="flex flex-col gap-2 p-6">
        <h2 className="text-xl font-medium text-primary">
          {t('billing.subscriptionTitle')}
        </h2>
        <p className="text-sm font-normal text-primary/60">
          {t('billing.subscriptionDescription')}
        </p>
      </div>

      <div className="flex w-full flex-col gap-2 p-6 pt-0">
        {subscriptionProducts.map((product) => (
          <div
            key={product.id}
            tabIndex={0}
            role="button"
            className={`flex w-full select-none items-center justify-between rounded-md border border-border p-4 hover:border-primary/60 ${
              selectedSubscriptionId === product.id ? 'border-primary/60' : ''
            }`}
            onClick={() => setSelectedSubscriptionId(product.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setSelectedSubscriptionId(product.id)
            }}
          >
            <span className="text-base font-medium text-primary">
              {product.name}
            </span>
            <span className="rounded-md bg-primary/10 px-1.5 text-sm font-medium text-primary/80">
              {(product.amountCents / 100).toFixed(0)}{' '}
              {product.currency.toUpperCase()}
            </span>
          </div>
        ))}
      </div>

      <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 py-3 dark:bg-card">
        <p className="text-sm font-normal text-primary/60">
          {t('billing.subscriptionNote')}
        </p>
        <Button
          type="button"
          size="sm"
          onClick={() => {
            if (selectedSubscriptionId) onSubscribe(selectedSubscriptionId)
          }}
          disabled={!selectedSubscriptionId}
        >
          {t('billing.subscribeLabel')}
        </Button>
      </div>
    </div>
  )
}

interface BillingEmailSectionProps {
  readonly user: User
}

function BillingEmailSection({ user }: BillingEmailSectionProps) {
  const { t } = useTranslation()
  const [email, setEmail] = useState(user.wallet?.billingEmail ?? '')
  const [saved, setSaved] = useState(false)

  const { mutate: save, isPending } = useMutation({
    mutationFn: useConvexAction(
      api.features.billing.billing_settings.updateMyBillingEmail,
    ),
    onSuccess: () => setSaved(true),
  })

  const isAdmin = user.role === 'owner' || user.role === 'admin'
  if (!isAdmin) return null

  return (
    <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
      <div className="flex w-full flex-col gap-4 rounded-lg p-6">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-medium text-primary">
            {t('billing.billingEmailTitle')}
          </h2>
          <p className="text-sm font-normal text-primary/60">
            {t('billing.billingEmailDescription')}
          </p>
        </div>

        <Input
          type="email"
          placeholder={user.email ?? t('billing.billingEmailPlaceholder')}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            setSaved(false)
          }}
          className="w-64 bg-transparent"
        />

        {saved && (
          <p className="text-sm text-primary/60">
            {t('billing.billingEmailSaved')}
          </p>
        )}
      </div>
      <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 dark:bg-card">
        <p className="text-sm font-normal text-primary/60">
          {t('billing.billingEmailHint')}
        </p>
        <Button
          type="button"
          size="sm"
          disabled={isPending || !email}
          onClick={() => save({ email })}
        >
          {isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t('billing.billingEmailSave')
          )}
        </Button>
      </div>
    </div>
  )
}

interface AutoTopUpSectionProps {
  readonly wallet: Wallet
  readonly topUpProducts: NormalizedProduct[]
}

function AutoTopUpSection({ wallet, topUpProducts }: AutoTopUpSectionProps) {
  const { t } = useTranslation()
  const [enabled, setEnabled] = useState(wallet.autoTopUpEnabled ?? false)
  const [thresholdInterviews, setThresholdInterviews] = useState(
    String(wallet.autoTopUpThresholdInterviews ?? 5),
  )
  const [productId, setProductId] = useState<string | undefined>(
    wallet.autoTopUpProductId ?? topUpProducts[0]?.id,
  )
  const [saved, setSaved] = useState(false)

  const { mutate: save, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.features.billing.billing_settings.updateMyAutoTopUpSettings,
    ),
    onSuccess: () => setSaved(true),
  })

  const hasPolarCustomer = wallet.polarCustomerId != null

  const handleSave = (): void => {
    setSaved(false)
    save({
      autoTopUpEnabled: enabled,
      autoTopUpThresholdInterviews: Math.max(
        1,
        Number(thresholdInterviews) || 5,
      ),
      autoTopUpProductId: productId ?? null,
    })
  }

  return (
    <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
      <div className="flex w-full flex-col gap-4 p-6">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-medium text-primary">
            {t('billing.autoTopUpTitle')}
          </h2>
          <p className="text-sm font-normal text-primary/60">
            {t('billing.autoTopUpDescription')}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            checked={enabled}
            onCheckedChange={(checked) => {
              setEnabled(checked)
              setSaved(false)
            }}
          />
          <span className="text-sm text-primary/80">
            {enabled
              ? t('billing.autoTopUpEnabled')
              : t('billing.autoTopUpDisabled')}
          </span>
        </div>

        {enabled && (
          <div className="flex flex-col gap-4">
            {!hasPolarCustomer && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                {t('billing.autoTopUpNoPaymentMethod')}
              </p>
            )}

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-primary">
                {t('billing.autoTopUpThresholdLabel')}
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  value={thresholdInterviews}
                  onChange={(e) => {
                    setThresholdInterviews(e.target.value)
                    setSaved(false)
                  }}
                  className="w-24 bg-transparent"
                />
                <span className="text-sm text-primary/60">
                  {t('billing.autoTopUpThresholdUnit')}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-primary">
                {t('billing.autoTopUpProductLabel')}
              </label>
              <div className="flex flex-col gap-2">
                {topUpProducts.map((product) => (
                  <div
                    key={product.id}
                    tabIndex={0}
                    role="button"
                    className={`flex w-full select-none items-center justify-between rounded-md border border-border p-3 hover:border-primary/60 ${
                      productId === product.id ? 'border-primary/60' : ''
                    }`}
                    onClick={() => {
                      setProductId(product.id)
                      setSaved(false)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setProductId(product.id)
                        setSaved(false)
                      }
                    }}
                  >
                    <span className="text-sm font-medium text-primary">
                      {product.name}
                    </span>
                    <span className="rounded-md bg-primary/10 px-1.5 text-xs font-medium text-primary/80">
                      {(product.amountCents / 100).toFixed(0)}{' '}
                      {product.currency.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {saved && (
          <p className="text-sm text-primary/60">
            {t('billing.autoTopUpSaved')}
          </p>
        )}
      </div>
      <div className="flex min-h-14 w-full items-center justify-end rounded-lg rounded-t-none border-t border-border bg-secondary px-6 py-3 dark:bg-card">
        <Button
          type="button"
          size="sm"
          disabled={isPending}
          onClick={handleSave}
        >
          {isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t('billing.autoTopUpSave')
          )}
        </Button>
      </div>
    </div>
  )
}

export function BillingSettingsPage() {
  const { t } = useTranslation()
  const { data: user } = useQuery(
    convexQuery(api.features.user.user.getMyCurrentUser, {}),
  )
  const { topUpProducts, subscriptionProducts } = useNormalizedProducts()
  const { redirectToCheckout, redirectToCustomerPortal } = useBillingActions()

  const [selectedTopUpId, setSelectedTopUpId] = useState<string | undefined>(
    undefined,
  )

  const handleTopUp = async (): Promise<void> => {
    if (!selectedTopUpId) return
    await redirectToCheckout({
      productIds: [selectedTopUpId],
      origin: window.location.origin,
      successUrl: `${window.location.origin}/dashboard/checkout`,
    })
  }

  const handleSubscribe = async (productId: string): Promise<void> => {
    await redirectToCheckout({
      productIds: [productId],
      origin: window.location.origin,
      successUrl: `${window.location.origin}/dashboard/checkout`,
    })
  }

  if (!user?.wallet) return null

  const isOwner = user.role === 'owner'
  const isAdminOrOwner = user.role === 'owner' || user.role === 'admin'

  return (
    <div className="flex h-full w-full flex-col gap-6">
      <BalanceSection wallet={user.wallet} />

      <BillingEmailSection user={user} />

      {isAdminOrOwner && (
        <AutoTopUpSection wallet={user.wallet} topUpProducts={topUpProducts} />
      )}

      <SubscriptionSection
        subscription={user.subscription}
        subscriptionProducts={subscriptionProducts}
        onSubscribe={handleSubscribe}
        onManage={redirectToCustomerPortal}
        isOwner={isOwner}
      />

      {isAdminOrOwner && (
        <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
          <div className="flex flex-col gap-2 p-6">
            <h2 className="text-xl font-medium text-primary">
              {t('billing.topUpTitle')}
            </h2>
            <p className="text-sm font-normal text-primary/60">
              {t('billing.topUpDescription')}
            </p>
          </div>

          <div className="flex w-full flex-col gap-2 p-6 pt-0">
            {topUpProducts.map((product) => (
              <TopUpProductCard
                key={product.id}
                product={product}
                selected={selectedTopUpId === product.id}
                onSelect={setSelectedTopUpId}
              />
            ))}
          </div>

          <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 py-3 dark:bg-card">
            <p className="text-sm font-normal text-primary/60">
              {t('billing.topUpNote')}
            </p>
            <Button
              type="button"
              size="sm"
              onClick={handleTopUp}
              disabled={!selectedTopUpId}
            >
              {t('billing.topUpLabel')}
            </Button>
          </div>
        </div>
      )}

      {isOwner && (
        <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
          <div className="flex flex-col gap-2 p-6">
            <h2 className="text-xl font-medium text-primary">
              {t('billing.invoiceHistoryTitle')}
            </h2>
            <p className="text-sm font-normal text-primary/60">
              {t('billing.invoiceHistoryDescription')}
            </p>
          </div>

          <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 py-3 dark:bg-card">
            <p className="text-sm font-normal text-primary/60">
              {t('billing.redirectedToPolar')}
            </p>
            <Button type="button" size="sm" onClick={redirectToCustomerPortal}>
              {t('billing.viewInvoices')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
